import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Alert, Spinner } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

const sortOptions = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' }
];

function buildDiffRows(generatedSignals, originalSignals, sortDirection) {
  const normalizeAction = (action) => {
    if (action === null || action === undefined) return '';
    if (typeof action === 'string') return action;
    return `${action}`;
  };

  const generatedMap = new Map();
  generatedSignals.forEach((signal) => {
    const key = signal.Time;
    if (!generatedMap.has(key)) generatedMap.set(key, []);
    generatedMap.get(key).push({ Time: signal.Time, Action: normalizeAction(signal.Action) });
  });

  const originalMap = new Map();
  originalSignals.forEach((signal) => {
    const key = signal.Time;
    if (!originalMap.has(key)) originalMap.set(key, []);
    originalMap.get(key).push({ Time: signal.Time, Action: normalizeAction(signal.Action) });
  });

  const allKeys = new Set([...generatedMap.keys(), ...originalMap.keys()]);
  const rows = [];

  allKeys.forEach((timeKey) => {
    const genList = generatedMap.get(timeKey) || [];
    const origList = originalMap.get(timeKey) || [];
    const count = Math.max(genList.length, origList.length, 1);

    for (let index = 0; index < count; index += 1) {
      const gen = genList[index];
      const orig = origList[index];
      const timeMoment = moment.utc(timeKey);
      const timeMs = timeMoment.isValid() ? timeMoment.valueOf() : 0;

      let status = 'Different';
      if (gen?.Action && orig?.Action) {
        status = gen.Action === orig.Action ? 'Match' : 'Different';
      } else if (gen?.Action) {
        status = 'Generated only';
      } else if (orig?.Action) {
        status = 'Original only';
      }

      rows.push({ key: `${timeKey}-${index}`, timeKey, timeMs, generatedAction: gen?.Action || '', originalAction: orig?.Action || '', status });
    }
  });

  rows.sort((a, b) => (sortDirection === 'asc' ? a.timeMs - b.timeMs : b.timeMs - a.timeMs));
  return rows;
}

function buildSummary(diffRows) {
  if (!diffRows.length) return null;
  const counts = { total: diffRows.length, match: 0, different: 0, generatedOnly: 0, originalOnly: 0 };
  diffRows.forEach((row) => {
    if (row.status === 'Match') counts.match += 1;
    else if (row.status === 'Generated only') counts.generatedOnly += 1;
    else if (row.status === 'Original only') counts.originalOnly += 1;
    else counts.different += 1;
  });
  return counts;
}

function renderStatusBadge(status) {
  if (status === 'Match') return <span className="badge badge-success" style={{ color: '#000' }}>Match</span>;
  if (status === 'Generated only') return <span className="badge badge-primary" style={{ color: '#000' }}>Generated only</span>;
  if (status === 'Original only') return <span className="badge badge-info" style={{ color: '#000' }}>Original only</span>;
  return <span className="badge badge-warning" style={{ color: '#000' }}>Different</span>;
}

function formatDuration(duration) {
  if (!duration) return '';
  if (typeof duration === 'string') return duration;
  return duration.toString();
}

function TagComparePanel({ tagResult, sortDirection, leftLabel = 'Generated', rightLabel = 'Original' }) {
  const diffRows = useMemo(
    () => buildDiffRows(tagResult.Generated?.Signals || [], tagResult.Original?.Signals || [], sortDirection),
    [tagResult, sortDirection]
  );
  const summary = useMemo(() => buildSummary(diffRows), [diffRows]);

  return (
    <div>
      {summary && (
        <div className="alert alert-light border">
          <div className="row text-center">
            <div className="col-sm-3"><strong>Total:</strong> {summary.total}</div>
            <div className="col-sm-3"><strong>Matches:</strong> {summary.match}</div>
            <div className="col-sm-3"><strong>Different:</strong> {summary.different}</div>
            <div className="col-sm-3"><strong>Only in one:</strong> {summary.generatedOnly + summary.originalOnly}</div>
          </div>
        </div>
      )}

      {diffRows.length === 0 ? (
        <Alert color="info" fade={false}>No signals found to compare.</Alert>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-sm">
            <thead>
              <tr>
                <th style={{ width: '160px' }}>Time</th>
                <th style={{ width: '160px' }}>{leftLabel}</th>
                <th style={{ width: '160px' }}>{rightLabel}</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {diffRows.map((row) => {
                const formattedTime = moment.utc(row.timeKey).local().format('L');
                let rowClass = '';
                if (row.status === 'Match') rowClass = 'table-success';
                else if (row.status === 'Different') rowClass = 'table-warning';
                else rowClass = 'table-info';
                return (
                  <tr key={row.key} className={rowClass}>
                    <td>{formattedTime}</td>
                    <td>{row.generatedAction || '-'}</td>
                    <td>{row.originalAction || '-'}</td>
                    <td>{renderStatusBadge(row.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <details className="mt-3">
        <summary className="mb-2">Raw signal files</summary>
        <div className="row">
          <div className="col-md-6">
            <h6>{leftLabel}</h6>
            <pre className="bg-light p-2 border" style={{ maxHeight: '240px', overflow: 'auto' }}>
              {tagResult.Generated?.SignalFileContent || 'No content'}
            </pre>
          </div>
          <div className="col-md-6">
            <h6>{rightLabel}</h6>
            <pre className="bg-light p-2 border" style={{ maxHeight: '240px', overflow: 'auto' }}>
              {tagResult.Original?.SignalFileContent || 'No content'}
            </pre>
          </div>
        </div>
      </details>
    </div>
  );
}

export const AdminCompareSignals = () => {
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [activeTag, setActiveTag] = useState(null);

  const [comparingInternal, setComparingInternal] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const [internalCompareResult, setInternalCompareResult] = useState(null);
  const [activeInternalTag, setActiveInternalTag] = useState(null);

  useEffect(() => { loadModels(); }, []);

  async function loadModels() {
    try {
      setLoadingModels(true);
      const list = await apiClient.getModelsList();
      setModels(list || []);
      if (list && list.length > 0) {
        setSelectedModelId(list[0].Id?.toString() || '');
      }
    } catch (exc) {
      console.error('Failed to load models', exc);
      setError(exc.message || 'Failed to load models');
    } finally {
      setLoadingModels(false);
    }
  }

  async function handleCompare() {
    if (!selectedModelId) {
      setError('Please select a model first');
      return;
    }

    try {
      setError(null);
      setCompareResult(null);
      setActiveTag(null);
      setComparing(true);

      const result = await apiClient.compareModelSignals(parseInt(selectedModelId, 10));
      setCompareResult(result);
      if (result?.Tags?.length > 0) {
        setActiveTag(result.Tags[0].Tag);
      }
    } catch (exc) {
      console.error('Failed to compare signals', exc);
      setError(exc.message || 'Failed to compare signals');
    } finally {
      setComparing(false);
    }
  }

  async function handleCompareVsInternal() {
    if (!selectedModelId) {
      setInternalError('Please select a model first');
      return;
    }

    try {
      setInternalError(null);
      setInternalCompareResult(null);
      setActiveInternalTag(null);
      setComparingInternal(true);

      const result = await apiClient.compareModelSignalsVsInternal(parseInt(selectedModelId, 10));
      setInternalCompareResult(result);
      if (result?.Tags?.length > 0) {
        setActiveInternalTag(result.Tags[0].Tag);
      }
    } catch (exc) {
      console.error('Failed to compare VM vs internal signals', exc);
      setInternalError(exc.message || 'Failed to compare VM vs internal signals');
    } finally {
      setComparingInternal(false);
    }
  }

  const activeTagResult = compareResult?.Tags?.find((t) => t.Tag === activeTag) || null;
  const activeInternalTagResult = internalCompareResult?.Tags?.find((t) => t.Tag === activeInternalTag) || null;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Compare Signals</h2>
      </div>

      <div>
        <div className="row align-items-end">
          <div className="col-md-6">
            <label className="form-label">Model</label>
            <select
              className="form-control"
              value={selectedModelId}
              onChange={(e) => setSelectedModelId(e.target.value)}
              disabled={loadingModels || comparing}
            >
              <option value="">-- Select a model --</option>
              {models.map((model) => (
                <option key={model.Id} value={model.Id}>
                  {model.Name || model.FileName || `Model #${model.Id}`} (ID: {model.Id})
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Sort order</label>
            <select
              className="form-control"
              value={sortDirection}
              onChange={(e) => setSortDirection(e.target.value)}
              disabled={comparing}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <Button color="primary" onClick={handleCompare} disabled={comparing || comparingInternal || loadingModels}>
              {comparing ? (
                <><Spinner size="sm" className="me-2" />Comparing...</>
              ) : (
                'DB vs Original CTRAC'
              )}
            </Button>
            {' '}
            <Button color="secondary" onClick={handleCompareVsInternal} disabled={comparing || comparingInternal || loadingModels}>
              {comparingInternal ? (
                <><Spinner size="sm" className="me-2" />Comparing...</>
              ) : (
                'VM vs Internal Code'
              )}
            </Button>
          </div>
        </div>

        {error && (
          <Alert color="danger" className="mt-3" fade={false}>{error}</Alert>
        )}

        {internalError && (
          <Alert color="danger" className="mt-3" fade={false}>{internalError}</Alert>
        )}

        {compareResult && (
          <div className="mt-4">
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header"><strong>Generated CTRAC</strong></div>
                  <div className="card-body py-2">
                    <div><strong>Duration:</strong> {formatDuration(compareResult.GeneratedExecutionDuration)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header"><strong>Original CTRAC</strong></div>
                  <div className="card-body py-2">
                    <div><strong>Duration:</strong> {formatDuration(compareResult.OriginalExecutionDuration)}</div>
                  </div>
                </div>
              </div>
            </div>

            {compareResult.Tags && compareResult.Tags.length > 0 && (
              <>
                <ul className="nav nav-tabs mb-3">
                  {compareResult.Tags.map((tagResult) => (
                    <li key={tagResult.Tag} className="nav-item">
                      <button
                        className={`nav-link${activeTag === tagResult.Tag ? ' active' : ''}`}
                        onClick={() => setActiveTag(tagResult.Tag)}
                      >
                        {tagResult.Tag}
                        {' '}
                        <small className="text-muted">
                          ({tagResult.Generated?.Signals?.length || 0} / {tagResult.Original?.Signals?.length || 0})
                        </small>
                      </button>
                    </li>
                  ))}
                </ul>

                {activeTagResult && (
                  <TagComparePanel
                    key={activeTagResult.Tag}
                    tagResult={activeTagResult}
                    sortDirection={sortDirection}
                    leftLabel="Generated CTRAC"
                    rightLabel="Original CTRAC"
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* ── VM vs Internal Code comparison ── */}
        {internalCompareResult && (
          <div className="mt-5">
            <h4>VM vs Internal Code</h4>
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header"><strong>VM (wwms.exe)</strong></div>
                  <div className="card-body py-2">
                    <div><strong>Duration:</strong> {formatDuration(internalCompareResult.GeneratedExecutionDuration)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header"><strong>Internal .NET Code</strong></div>
                  <div className="card-body py-2">
                    <div><strong>Duration:</strong> {formatDuration(internalCompareResult.OriginalExecutionDuration)}</div>
                  </div>
                </div>
              </div>
            </div>

            {internalCompareResult.Tags && internalCompareResult.Tags.length > 0 && (
              <>
                <ul className="nav nav-tabs mb-3">
                  {internalCompareResult.Tags.map((tagResult) => (
                    <li key={tagResult.Tag} className="nav-item">
                      <button
                        className={`nav-link${activeInternalTag === tagResult.Tag ? ' active' : ''}`}
                        onClick={() => setActiveInternalTag(tagResult.Tag)}
                      >
                        {tagResult.Tag}
                        {' '}
                        <small className="text-muted">
                          ({tagResult.Generated?.Signals?.length || 0} / {tagResult.Original?.Signals?.length || 0})
                        </small>
                      </button>
                    </li>
                  ))}
                </ul>

                {activeInternalTagResult && (
                  <TagComparePanel
                    key={activeInternalTagResult.Tag}
                    tagResult={activeInternalTagResult}
                    sortDirection={sortDirection}
                    leftLabel="VM (wwms.exe)"
                    rightLabel="Internal .NET"
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
